// Behaviour for the shared frame (frame/header.html + frame/footer.html).
// Pure DOM + event delegation, so it survives the SPA re-mounting the page
// HTML. Safe to call more than once.
let started = false

export function initFrame() {
  if (started) return
  started = true
  const body = document.body
  const closeMenu = () => {
    body.classList.remove('pbf-menu')
    document.querySelector('.pbf-burger')?.setAttribute('aria-expanded', 'false')
  }

  // Encryption-style text scramble on the top-level menu items only (not the
  // dropdown panels). Delegated, so it works on every page and survives the SPA
  // re-mounting the header. The text node is restored exactly when it finishes.
  const GL = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#$%&*+=<>/?'
  const scramble = (a) => {
    if (a._sc || matchMedia('(prefers-reduced-motion:reduce)').matches) return
    const tn = [...a.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim())
    if (!tn) return
    const o = tn.nodeValue
    tn._o = o // real text, read by pb-transition.js if the link is clicked mid-scramble
    a._sc = true
    const w = a.offsetWidth
    a.style.minWidth = w + 'px'
    const t0 = performance.now()
    const f = (now) => {
      const p = Math.min(1, (now - t0) / 520)
      let out = ''
      for (let i = 0; i < o.length; i++) out += o[i] === ' ' || i < p * o.length * 1.05 ? o[i] : GL[Math.floor(Math.random() * GL.length)]
      tn.nodeValue = out
      if (p < 1) requestAnimationFrame(f)
      else { tn.nodeValue = o; a.style.minWidth = ''; a._sc = false }
    }
    requestAnimationFrame(f)
  }
  document.addEventListener('pointerover', (e) => {
    const a = e.target.closest?.('.pbf-nav a.pbf-l')
    if (a && !a.contains(e.relatedTarget)) scramble(a)
  })

  // Desktop menu: a top-level item with a dropdown is a hover trigger, not a
  // link - clicking it must neither navigate nor leave its panel stuck open.
  document.addEventListener(
    'click',
    (e) => {
      const top = e.target.closest?.('.pbf-has-dd > a.pbf-l')
      if (!top) return
      e.preventDefault()
      e.stopImmediatePropagation()
      top.blur()
    },
    true,
  )

  document.addEventListener(
    'click',
    (e) => {
      // Links into the standalone home page ("/", "/#network" ...) must be a
      // real page load: the SPA router would otherwise swap in its own home.
      const home = e.target.closest?.('a[data-pbf-home]')
      if (home && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        const onHome = document.documentElement.classList.contains('pbf-home')
        const url = new URL(home.getAttribute('href'), location.origin)
        if (!(onHome && url.pathname === '/' && url.hash && document.querySelector(url.hash))) {
          e.preventDefault()
          e.stopImmediatePropagation()
          closeMenu()
          location.assign(url.href)
          return
        }
      }
      const burger = e.target.closest?.('.pbf-burger')
      if (burger) {
        const on = body.classList.toggle('pbf-menu')
        burger.setAttribute('aria-expanded', String(on))
        return
      }
      if (body.classList.contains('pbf-menu') && e.target.closest?.('.pbf-mob a')) closeMenu()
    },
    true,
  )
  // The "/" route belongs to the standalone home page, never to the SPA's own
  // (old) home. Every way of reaching it from an SPA page must be a real page
  // load: in-page links (breadcrumb "Home", logos...), history.pushState from
  // the router, and the browser Back/Forward buttons.
  const onStandaloneHome = () => document.documentElement.classList.contains('pbf-home')
  const isHomePath = (u) => new URL(u, location.href).pathname.replace(/\/+$/, '') === ''
  document.addEventListener(
    'click',
    (e) => {
      if (onStandaloneHome() || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = e.target.closest?.('a[href]')
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return
      const url = new URL(a.getAttribute('href'), location.href)
      if (url.origin !== location.origin || !isHomePath(url.href)) return
      e.preventDefault()
      e.stopImmediatePropagation()
      closeMenu()
      location.assign(url.href)
    },
    true,
  )
  for (const m of ['pushState', 'replaceState']) {
    const orig = history[m]
    history[m] = function (state, title, url) {
      if (url != null && !onStandaloneHome() && isHomePath(url)) { location.assign(new URL(url, location.href).href); return }
      return orig.apply(this, arguments)
    }
  }
  window.addEventListener('popstate', () => {
    if (!onStandaloneHome() && isHomePath(location.href)) location.reload()
  })
  window.addEventListener('keydown', (e) => e.key === 'Escape' && closeMenu())
  window.addEventListener('popstate', closeMenu)
  window.addEventListener('resize', () => innerWidth >= 1280 && closeMenu())

  // The bar stays in place on scroll (it used to slide away on scroll down).
}
